import React, { useCallback, useContext, useRef, useState } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Grid } from '@material-ui/core';
import { Box, CircularProgress } from '@kareo/design-system';
import rollbar from 'shared-assets/src/errors/errors';
import ModalCard from 'shared-assets/components/Modal/ModalCard';
import { PatientContext } from 'shared-assets/facesheet-ui/components/Facesheet/Container/Facesheet';
import { useSnackbar } from 'notistack';
import { getPhotoUploadHeaderText } from 'shared-assets/facesheet-ui/utils/patientHelper';
import { canvasPreview, setCropPosition, } from 'shared-assets/facesheet-ui/utils/photoPreview';
import BrowsePhoto from 'shared-assets/facesheet-ui/components/Facesheet/Header/PhotoUpload/BrowsePhoto';
import { getPatientPhotoUrlForEdit, savePatientPhoto, } from 'shared-assets/facesheet-ui/service/patient/service';
import { PHOTO_UPLOAD_ALERT } from 'shared-assets/facesheet-ui/service/patient/types';
import { StyledBox } from 'shared-assets/facesheet-ui/utils/styledFacesheet';
import PhotoUploadFooter from 'shared-assets/facesheet-ui/components/Facesheet/Header/PhotoUpload/PhotoUploadFooter';
var PatientPhotoUpload = function (_a) {
    var patientId = _a.patientId, isPhotoModalOpen = _a.isPhotoModalOpen, closePhotoModal = _a.closePhotoModal, hasPatientPhoto = _a.hasPatientPhoto, reloadPatientPhoto = _a.reloadPatientPhoto;
    var patient = useContext(PatientContext);
    var patientPhotoUrl = getPatientPhotoUrlForEdit(patientId);
    var _b = useState(true), loading = _b[0], setLoading = _b[1];
    var _c = useState(null), photo = _c[0], setPhoto = _c[1];
    var _d = useState(patientPhotoUrl), preview = _d[0], setPreview = _d[1];
    var _e = useState(false), isDiscarded = _e[0], setIsDiscarded = _e[1];
    var _f = useState(false), isCropping = _f[0], setIsCropping = _f[1];
    var _g = useState(false), isCropped = _g[0], setIsCropped = _g[1];
    var previewCanvasRef = useRef(null);
    var imgRef = useRef(null);
    var _h = useState(), crop = _h[0], setCrop = _h[1];
    var _j = useState(), defaultCrop = _j[0], setDefaultCrop = _j[1];
    var _k = useState(), completedCrop = _k[0], setCompletedCrop = _k[1];
    var enqueueSnackbar = useSnackbar().enqueueSnackbar;
    var _l = useState(false), uploading = _l[0], setUploading = _l[1];
    var onDrop = useCallback(function (acceptedFiles) {
        if (acceptedFiles.length > 0) {
            var photo_1 = acceptedFiles[0];
            setPhoto(photo_1);
            setPreview(photo_1.preview);
            setIsDiscarded(false);
            setIsCropping(true);
        }
    }, [photo]);
    var handleDiscardOrRedo = function () {
        if (isCropping) {
            discardPhoto();
        }
        else if (isCropped) {
            redoCrop();
        }
    };
    var discardPhoto = function () {
        setIsDiscarded(true);
        setPhoto(null);
        setPreview('');
        setCrop(undefined);
        setIsCropping(false);
        setCompletedCrop(undefined);
    };
    var redoCrop = function () {
        setCrop(defaultCrop);
        setDefaultCrop(undefined);
        setIsCropping(true);
        setIsCropped(false);
    };
    var handleCropOrSave = function () {
        if (isCropping) {
            cropPhotoAndPreview();
        }
        else if (isCropped) {
            savePhoto();
        }
    };
    var cropPhotoAndPreview = function () {
        setIsCropped(true);
        setIsCropping(false);
        setDefaultCrop(crop);
        if (imgRef.current && previewCanvasRef.current && completedCrop) {
            canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
        }
    };
    var savePhoto = function () {
        if (previewCanvasRef.current) {
            setUploading(true);
            var photoData = previewCanvasRef.current.toDataURL('image/png');
            var imgDataToSave = photoData.substring(photoData.indexOf(',') + 1);
            savePatientPhoto(patientId, imgDataToSave)
                .then(function (res) {
                enqueueSnackbar(PHOTO_UPLOAD_ALERT.success, {
                    variant: 'success',
                });
                reloadPatientPhoto();
                closePhotoModal();
            })
                .catch(function (error) {
                enqueueSnackbar(PHOTO_UPLOAD_ALERT.error, {
                    variant: 'error',
                });
                rollbar.error(error);
            })
                .finally(function () { return setUploading(false); });
        }
    };
    var onImageLoad = function (event) {
        var _a = event.currentTarget, width = _a.width, height = _a.height;
        setCrop(setCropPosition(width, height));
        setIsCropping(true);
        setLoading(false);
    };
    return (React.createElement(ModalCard, { open: isPhotoModalOpen, onClose: closePhotoModal, header: getPhotoUploadHeaderText(patient, hasPatientPhoto), footer: React.createElement(PhotoUploadFooter, { closePhotoModal: closePhotoModal, hasPatientPhoto: hasPatientPhoto, isDiscarded: isDiscarded, isPhotoDropped: !!photo, handleDiscardOrRedo: handleDiscardOrRedo, isCropping: isCropping, handleCropOrSave: handleCropOrSave, isCropped: isCropped, isUploading: uploading }) },
        React.createElement(StyledBox, null, (!hasPatientPhoto && !photo) || (hasPatientPhoto && isDiscarded) ? (React.createElement(BrowsePhoto, { onDrop: onDrop })) : (React.createElement(React.Fragment, null,
            React.createElement(Grid, { container: true, justify: "center", alignItems: "center" },
                React.createElement(Grid, { item: true },
                    loading && (React.createElement(Box, { m: 2, width: 320, height: 320, display: "flex", justifyContent: "center", alignItems: "center" },
                        React.createElement(CircularProgress, { size: 40, color: "primary" }))),
                    React.createElement(ReactCrop, { crop: crop, onChange: function (_, percentCrop) { return setCrop(percentCrop); }, onComplete: function (c) { return setCompletedCrop(c); }, aspect: 1, minWidth: 128, minHeight: 128, style: {
                            margin: '1rem',
                            maxHeight: hasPatientPhoto && !photo ? '320px' : '100%',
                            maxWidth: hasPatientPhoto && !photo ? '320px' : '100%',
                            display: isCropped || loading ? 'none' : 'block',
                        } },
                        React.createElement("img", { ref: imgRef, src: preview, onLoad: onImageLoad })))),
            completedCrop && (React.createElement(Grid, { container: true, justify: "center", alignItems: "center" },
                React.createElement(Grid, { item: true },
                    React.createElement("canvas", { ref: previewCanvasRef, style: {
                            margin: '1rem',
                            objectFit: 'contain',
                            width: completedCrop.width,
                            height: completedCrop.height,
                            display: isCropped ? 'block' : 'none',
                        } })))))))));
};
export default PatientPhotoUpload;
